import { CraMonth, DealRoom, Evaluation, MissionCanvas, Moscow, ProjectContext, Raci, RaciMatrixEntry, RaciRole, RaciTask, Sipoc, TreasuryProfile, UserProfile } from '@shared/types';
import { ProposalRecord, ProposalTemplate, createProposalDraft } from '@shared/domain/proposal';
import { generateId } from '@shared/utils';

const DEFAULT_ROLES = ['Freelance', 'Client', 'Sponsor'];

export function averageEvaluationScore(scores: Evaluation['scores']): number {
  const values = Object.values(scores);
  const total = values.reduce((sum, value) => sum + value, 0);
  return Math.round((total / values.length) * 20);
}

export function verdictFromScore(score: number): Evaluation['verdict'] {
  if (score >= 75) return 'GO';
  if (score >= 55) return 'CONDITIONAL';
  return 'NO_GO';
}

export function recommendationsFromScore(score: number, stress?: Evaluation['stress']): string[] {
  const recs: string[] = [];
  if (score >= 75) recs.push('Mission prioritaire', 'Passer au cadrage SIPOC');
  else if (score >= 55) recs.push('Clarifier le perimetre', 'Valider les risques avant engagement');
  else recs.push('Requalifier le besoin', 'Renegocier le scope ou decliner la mission');

  if (stress) {
    const stressAvg = (Object.values(stress).reduce((s, v) => s + v, 0) / 4) * 20;
    if (stressAvg > 75) recs.push('Stress critique — renegocier les conditions ou decliner la mission');
    else if (stressAvg > 50) recs.push('Stress eleve — anticiper le cout personnel sur la duree');
    if (stress.clientDependency >= 4) recs.push('Forte dependance client — diversifier le pipeline');
    if (stress.rhythmIntensity >= 4) recs.push('Rythme intense — prevoir des marges de recuperation');
  }

  return recs;
}

export function createSipocSeed(project: ProjectContext, evaluation?: Evaluation | null): Sipoc {
  return {
    id: generateId(),
    projectId: project.id,
    name: project.name || 'Processus mission',
    owner: project.owner || project.client || '',
    suppliers: { items: [] },
    inputs: {
      items: project.description
        ? [{ id: generateId(), title: project.description, order: 0 }]
        : [],
    },
    process: { items: [] },
    outputs: { items: [] },
    customers: {
      items: project.client
        ? [{ id: generateId(), title: project.client, order: 0 }]
        : [],
    },
    projectScope: project.scope || '',
    scope: (() => {
      if (!evaluation || !evaluation.totalScore || evaluation.totalScore < 75) return 'SIMPLE';
      const stressAvg = evaluation.stress
        ? (Object.values(evaluation.stress).reduce((s, v) => s + v, 0) / 4) * 20
        : 0;
      return stressAvg > 60 ? 'SIMPLE' : 'TECHNICAL';
    })(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function createMoscowSeed(project: ProjectContext, sipoc?: Sipoc | null): Moscow {
  const outputItems = sipoc?.outputs.items ?? [];
  const processItems = sipoc?.process.items ?? [];

  const must = outputItems.map((item, index) => ({
    id: generateId(),
    title: item.title,
    description: item.description,
    effort: 1,
    order: index,
  }));

  const should = processItems.map((item, index) => ({
    id: generateId(),
    title: item.title,
    description: item.description,
    effort: 1,
    order: index,
  }));

  return {
    id: generateId(),
    projectId: project.id,
    name: project.name || 'Priorisation mission',
    budgetDays: 0,
    teamSize: 1,
    categories: {
      must: { items: must },
      should: { items: should },
      could: { items: [] },
      out: { items: [] },
      wont: { items: [] },
    },
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function createRaciSeed(project: ProjectContext, moscow?: Moscow | null): Raci {
  const roles: RaciRole[] = DEFAULT_ROLES.map((name) => ({
    id: generateId(),
    name,
  }));

  const seededTasks = [...(moscow?.categories.must.items ?? []), ...(moscow?.categories.should.items ?? [])];
  const tasks: RaciTask[] = seededTasks.map((item, index) => ({
    id: generateId(),
    title: item.title,
    description: item.description,
    mustHave: index < (moscow?.categories.must.items.length ?? 0),
    shouldHave: index >= (moscow?.categories.must.items.length ?? 0),
    order: index,
  }));

  const matrix: RaciMatrixEntry[] = [];
  tasks.forEach((task) => {
    if (roles[0]) matrix.push({ taskId: task.id, roleId: roles[0].id, assignment: 'R' });
    if (roles[1]) matrix.push({ taskId: task.id, roleId: roles[1].id, assignment: 'A' });
    if (roles[2]) matrix.push({ taskId: task.id, roleId: roles[2].id, assignment: 'C' });
  });

  return {
    id: generateId(),
    projectId: project.id,
    name: project.name || 'Matrice RACI',
    roles,
    tasks,
    matrix,
    sponsor: project.owner || project.client || '',
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function createDealRoomSeed(
  project: ProjectContext,
  moscow?: Moscow | null,
  raci?: Raci | null,
): DealRoom {
  const mustItems = moscow?.categories.must.items ?? [];
  const shouldItems = moscow?.categories.should.items ?? [];
  const deliverables = mustItems.slice(0, 5).map((item) => item.title);
  const tasks = (raci?.tasks ?? []).slice(0, 5).map((task) => ({
    id: generateId(),
    title: task.title,
    status: 'todo' as const,
  }));

  return {
    id: generateId(),
    projectId: project.id,
    name: project.name || 'Deal Room',
    client: project.client || '',
    owner: project.owner || '',
    status: mustItems.length > 0 ? 'cadre' : 'prospect',
    objective: project.description || (project.name ? `Securiser la mission ${project.name}.` : ''),
    context: project.description || '',
    scope: project.scope || '',
    deliverables: deliverables.length ? deliverables : shouldItems.slice(0, 3).map((item) => item.title),
    tasks: tasks.length ? tasks : [{ id: generateId(), title: 'Valider le prochain jalon', status: 'todo' }],
    milestones: [],
    risks: project.budget ? [`Budget cible : ${project.budget} EUR a confirmer contractuellement`] : ['Budget et charge a confirmer'],
    decisions: ['Valider la prochaine synchro client'],
    nextSteps: '',
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function createCraSeed(project: ProjectContext, dealRoom?: DealRoom | null, year?: number, month?: number): CraMonth {
  const today = new Date();
  const targetYear = year ?? today.getFullYear();
  const targetMonth = month ?? today.getMonth();
  return {
    id: generateId(),
    projectId: project.id,
    year: targetYear,
    month: targetMonth,
    consultant: project.owner || '',
    client: project.client || '',
    mission: dealRoom?.name || project.name || 'Mission',
    tjm: project.tjm || 0,
    dayStates: {},
    workedDays: 0,
    leaveDays: 0,
    revenue: 0,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function parseLines(text: string | undefined): string[] {
  if (!text?.trim()) return [];
  return text.split('\n').map((l) => l.trim()).filter(Boolean);
}

// ── TKT-008 — Canvas → SIPOC ───────────────────────────────────────────────

export function createSipocFromCanvas(
  project: ProjectContext,
  canvas: MissionCanvas,
  evaluation?: Evaluation | null,
): Sipoc {
  const objectiveLines = parseLines(canvas.objective?.content);
  const stakeholderLines = parseLines(canvas.stakeholders?.content);
  const riskLines = parseLines(canvas.risks?.content);

  const outputs = objectiveLines.length ? [{ id: generateId(), title: objectiveLines[0], order: 0 }] : [];
  const suppliers = stakeholderLines.map((title, i) => ({ id: generateId(), title, order: i }));

  const processItems: Array<{ id: string; title: string; order: number }> = [];
  const constraintParts = [
    canvas.constraints?.budget ? `Budget : ${canvas.constraints.budget}` : '',
    canvas.constraints?.deadline ? `Délai : ${canvas.constraints.deadline}` : '',
    canvas.constraints?.other ? `Autres : ${canvas.constraints.other}` : '',
  ].filter(Boolean);
  if (constraintParts.length) {
    processItems.push({ id: generateId(), title: constraintParts.join(' · '), order: 0 });
  }

  return {
    id: generateId(),
    projectId: project.id,
    name: project.name || 'Processus mission',
    owner: project.owner || project.client || '',
    suppliers: { items: suppliers },
    inputs: { items: [] },
    process: { items: processItems },
    outputs: { items: outputs },
    customers: {
      items: project.client
        ? [{ id: generateId(), title: project.client, order: 0 }]
        : [],
    },
    projectScope: canvas.why?.content || project.scope || '',
    notes: riskLines,
    scope: (() => {
      if (!evaluation?.totalScore || evaluation.totalScore < 75) return 'SIMPLE';
      const stressAvg = evaluation.stress
        ? (Object.values(evaluation.stress).reduce((s, v) => s + v, 0) / 4) * 20
        : 0;
      return stressAvg > 60 ? 'SIMPLE' : 'TECHNICAL';
    })(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

// ── TKT-009 — Canvas → MoSCoW ──────────────────────────────────────────────

export function createMoscowFromCanvas(project: ProjectContext, canvas: MissionCanvas): Moscow {
  const objectiveLines = parseLines(canvas.objective?.content);
  const riskLines = parseLines(canvas.risks?.content);

  const must = objectiveLines.map((title, i) => ({ id: generateId(), title, description: '', effort: 1, order: i }));
  const out = riskLines.map((title, i) => ({ id: generateId(), title, description: '', effort: 1, order: i }));

  return {
    id: generateId(),
    projectId: project.id,
    name: project.name || 'Priorisation mission',
    budgetDays: 0,
    teamSize: 1,
    categories: {
      must: { items: must },
      should: { items: [] },
      could: { items: [] },
      out: { items: out },
      wont: { items: out },
    },
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

// ── TKT-010 — Canvas → Proposal ────────────────────────────────────────────

export function createProposalFromCanvas(project: ProjectContext, canvas: MissionCanvas, template?: ProposalTemplate): ProposalRecord {
  const successLines = parseLines(canvas.successCriteria?.content);

  const deliverables = successLines.slice(0, 5).map((label) => ({
    id: generateId(),
    label,
    description: '',
  }));

  return {
    ...createProposalDraft(),
    template: template || 'tech',
    mode: 'client',
    projectId: project.id,
    clientName: project.client || '',
    missionName: project.name || '',
    context: canvas.objective?.content || '',
    contextDetails: canvas.why?.content || '',
    deliverables,
    fixedPrice: project.budget || 0,
    pricingMode: project.budget ? 'fixed' : 'tjm',
  };
}

export function createTreasurySeed(project: ProjectContext, cra?: CraMonth | null, profile?: Pick<UserProfile, 'status' | 'tjm' | 'daysPerMonth' | 'eiCategory' | 'acre' | 'versementLiberatoire' | 'householdSituation' | 'children'> | null): TreasuryProfile {
  const isCraForfait = cra?.pricingMode === 'forfait';
  return {
    id: generateId(),
    projectId: project.id,
    status: profile?.status || 'sasu',
    pricingMode: isCraForfait ? 'prestation' : (cra?.pricingMode === 'tjm' ? 'tjm' : undefined),
    tarifPrestation: isCraForfait ? (cra?.tarifForfait ?? undefined) : undefined,
    joursPrestation: undefined,
    openingBalance: 5000,
    caYtd: cra?.revenue || 0,
    horizonMonths: 6,
    daysPerMonth: cra?.workedDays || project.daysPerMonth || profile?.daysPerMonth || 18,
    tjm: cra?.tjm || project.tjm || profile?.tjm || 0,
    taxStrategy: profile?.status === 'ei' ? 'remuneration' : profile?.status === 'portage' ? 'remuneration' : 'dividendes',
    salaryNetTarget: 0,
    dividendPayoutPct: 100,
    vatRegistered: false,
    eiCategory: profile?.eiCategory,
    acre: profile?.acre,
    versementLiberatoire: profile?.versementLiberatoire,
    householdSituation: profile?.householdSituation,
    children: profile?.children,
    charges: [
      { id: generateId(), label: 'Mutuelle', amount: 150, kind: 'fixe' },
      { id: generateId(), label: 'Comptable', amount: 120, kind: 'fixe' },
      { id: generateId(), label: 'Logiciels / abonnements', amount: 80, kind: 'fixe' },
    ],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  } as TreasuryProfile;
}
